import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

const formatAmount = (value) =>
  `PHP ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");
const todayString = () => new Date().toISOString().slice(0, 10);
const startOfMonthString = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

export default function AdminNeedsAttention() {
  const navigate = useNavigate();
  const [data, setData] = useState({
    payments: [],
    reports: [],
    archive_activity: [],
    data_quality: {},
    lease_alerts: [],
    reminders_summary: {},
  });
  const [loading, setLoading] = useState(true);
  const [workingAnnouncementId, setWorkingAnnouncementId] = useState(null);
  const [toast, setToast] = useState(null);
  const [fromDate, setFromDate] = useState(startOfMonthString());
  const [toDate, setToDate] = useState(todayString());

  const loadData = async (from = fromDate, to = toDate) => {
    try {
      const response = await api.get("/admin/operations/needs-attention", {
        params: { from, to },
      });
      setData(
        response.data || {
          payments: [],
          reports: [],
          archive_activity: [],
          data_quality: {},
          lease_alerts: [],
          reminders_summary: {},
        }
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(startOfMonthString(), todayString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(timeout);
  }, [toast]);

  const latestArchiveActionByEntity = useMemo(() => {
    const map = {};
    (data.archive_activity || []).forEach((item) => {
      const key = `${item.entity_type || ""}:${item.entity_id || ""}`;
      if (!map[key]) {
        map[key] = item.action;
      }
    });
    return map;
  }, [data.archive_activity]);

  const handleRestoreAnnouncement = async (announcementId) => {
    if (!announcementId) return;
    try {
      setWorkingAnnouncementId(announcementId);
      await api.put(`/admin/announcements/${announcementId}/restore`);
      setToast({ message: "Announcement restored.", type: "success" });
      await loadData();
    } catch (error) {
      console.error(error);
      setToast({ message: "Failed to restore announcement.", type: "error" });
    } finally {
      setWorkingAnnouncementId(null);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans text-white">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true"
          src={bgVideo}
          autoPlay
          muted
          loop
          playsInline
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-950/85" />
      </div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Admin Operations
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
              Needs Attention
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Focus queue for payment clarifications, urgent reports, and
              recent archive activity.
            </p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
              Period: {data.period?.label || `${fromDate} to ${toDate}`}
            </p>
          </div>

          
        </div>

        <div className="mb-5 grid gap-3 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl lg:grid-cols-[auto_auto_auto_auto] lg:items-center">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          />
          <button
            onClick={() => loadData(fromDate, toDate)}
            className="rounded-lg bg-teal-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-teal-500"
          >
            Apply Date Filter
          </button>
          <button
            onClick={() => {
              const start = startOfMonthString();
              const end = todayString();
              setFromDate(start);
              setToDate(end);
              loadData(start, end);
            }}
            className="rounded-lg border border-white/15 px-4 py-3 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
          >
            Reset to Current Month
          </button>
        </div>

        {loading ? (
          <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
            Loading attention queue...
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-3">
            <section className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
              <div className="mb-4 border-b border-white/10 pb-3">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                  Payments
                </p>
                <h2 className="mt-1 text-xl font-bold text-white">
                  Clarification + Rejected
                </h2>
                <button
                  onClick={() => navigate("/payments")}
                  className="mt-2 rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                >
                  Open Payment Queue
                </button>
              </div>
              <div className="space-y-3">
                {data.payments.length === 0 ? (
                  <p className="text-sm text-slate-400">No payment alerts.</p>
                ) : (
                  data.payments.slice(0, 10).map((payment) => (
                    <article
                      key={payment.id}
                      className="rounded-lg border border-white/10 bg-white/5 p-3"
                    >
                      <p className="font-semibold text-white">
                        {payment.tenant_name || "Tenant"}
                      </p>
                      <p className="mt-1 text-sm text-teal-200">
                        {formatAmount(payment.amount)}
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        {payment.property?.property_name || "Linked rental"}
                      </p>
                      <p className="mt-2 text-xs text-amber-200">
                        {payment.status === "needs_clarification"
                          ? "Needs clarification"
                          : "Rejected"}
                      </p>
                    </article>
                  ))
                )}
              </div>
            </section>

            <section className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
              <div className="mb-4 border-b border-white/10 pb-3">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                  Reports
                </p>
                <h2 className="mt-1 text-xl font-bold text-white">
                  Urgent + SLA Risk
                </h2>
                <button
                  onClick={() => navigate("/admin/reports")}
                  className="mt-2 rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                >
                  Open Reports
                </button>
              </div>
              <div className="space-y-3">
                {data.reports.length === 0 ? (
                  <p className="text-sm text-slate-400">No report alerts.</p>
                ) : (
                  data.reports.slice(0, 10).map((report) => (
                    <article
                      key={report.id}
                      className="cursor-pointer rounded-lg border border-white/10 bg-white/5 p-3 transition hover:border-teal-300/40 hover:bg-teal-500/10"
                      onClick={() => navigate(`/admin/reports?focus=${report.id}&action=edit`)}
                    >
                      <p className="font-semibold text-white">{report.subject}</p>
                      <p className="mt-1 text-xs text-slate-400">
                        {report.tenant_name || "Unknown Tenant"}
                      </p>
                      <p className="mt-1 text-xs text-slate-300">
                        Priority: {report.priority || "Normal"}
                      </p>
                      <p
                        className={`mt-2 inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${
                          report.sla_status === "overdue"
                            ? "border-red-300/40 bg-red-500/15 text-red-200"
                            : report.sla_status === "due_soon"
                            ? "border-amber-300/40 bg-amber-500/15 text-amber-200"
                            : "border-white/20 bg-white/5 text-slate-300"
                        }`}
                      >
                        {report.sla_status === "overdue"
                          ? "Overdue"
                          : report.sla_status === "due_soon"
                          ? "Due Soon"
                          : "On Track"}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/reports?focus=${report.id}&action=edit`);
                        }}
                        className="mt-3 rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                      >
                        Open Report
                      </button>
                    </article>
                  ))
                )}
              </div>
            </section>

            <section className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
              <div className="mb-4 border-b border-white/10 pb-3">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                  Leases
                </p>
                <h2 className="mt-1 text-xl font-bold text-white">
                  Expiring + Expired
                </h2>
                <button
                  onClick={() => navigate("/list-properties")}
                  className="mt-2 rounded-lg border border-white/15 px-3 py-1 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
                >
                  Open Properties
                </button>
              </div>
              <div className="space-y-3">
                {(data.lease_alerts || []).length === 0 ? (
                  <p className="text-sm text-slate-400">No lease alerts.</p>
                ) : (
                  (data.lease_alerts || []).slice(0, 10).map((lease) => (
                    <article
                      key={`${lease.property_id}-${lease.lease_end_date}`}
                      className="rounded-lg border border-white/10 bg-white/5 p-3"
                    >
                      <p className="font-semibold text-white">{lease.property_name}</p>
                      <p className="mt-1 text-xs text-slate-400">{lease.tenant_name || "Unknown tenant"}</p>
                      <p className="mt-1 text-xs text-slate-300">{lease.lease_end_date || "-"}</p>
                      <p className="mt-1 text-xs text-amber-200">
                        {lease.days_remaining < 0
                          ? `${Math.abs(lease.days_remaining)} days overdue`
                          : `${lease.days_remaining} days remaining`}
                      </p>
                    </article>
                  ))
                )}
              </div>
            </section>

            <section className="rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl">
              <div className="mb-4 border-b border-white/10 pb-3">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                  Activity
                </p>
                <h2 className="mt-1 text-xl font-bold text-white">
                  Archive + Restore
                </h2>
              </div>
              <div className="space-y-3">
                {data.archive_activity.length === 0 ? (
                  <p className="text-sm text-slate-400">No recent archive activity.</p>
                ) : (
                  data.archive_activity.slice(0, 10).map((activity) => {
                    const entityType = String(activity.entity_type || "").toLowerCase();
                    const entityKey = `${activity.entity_type || ""}:${activity.entity_id || ""}`;
                    const latestAction = latestArchiveActionByEntity[entityKey];
                    const isArchivedAnnouncementEvent =
                      entityType === "announcement" &&
                      activity.action === "announcement.archived";
                    const canRestore =
                      isArchivedAnnouncementEvent &&
                      latestAction === "announcement.archived";

                    return (
                      <article
                        key={activity.id}
                        className="rounded-lg border border-white/10 bg-white/5 p-3"
                      >
                        <p className="text-sm font-semibold text-white">
                          {activity.action}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          {activity.entity_type} #{activity.entity_id || "-"}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {activity.actor?.name || "System"} -{" "}
                          {formatDateTime(activity.created_at)}
                        </p>

                        {canRestore && (
                          <button
                            type="button"
                            onClick={() => handleRestoreAnnouncement(activity.entity_id)}
                            disabled={workingAnnouncementId === activity.entity_id}
                            className="mt-3 rounded-lg bg-teal-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {workingAnnouncementId === activity.entity_id
                              ? "Restoring..."
                              : "Restore"}
                          </button>
                        )}

                        {isArchivedAnnouncementEvent &&
                          latestAction === "announcement.restored" && (
                            <p className="mt-3 text-xs font-semibold text-teal-300">
                              Already restored
                            </p>
                          )}
                      </article>
                    );
                  })
                )}
              </div>
            </section>

          </div>
        )}

        {toast && (
          <div
            className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-sm font-semibold text-white shadow-2xl ${
              toast.type === "success" ? "bg-teal-600" : "bg-red-600"
            }`}
          >
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
}
